import React, { useState, useEffect } from 'react';
import { 
  Flame, 
  Plus, 
  Search, 
  Filter,
  Eye,
  Edit2,
  Trash2,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import axios from 'axios';

const API_URL = (window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL) || 'http://localhost:8001';

// Modal de création d'équipement
const CreateEquipementModal = ({ sites, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    type_equipement: 'extincteur',
    marque: '',
    modele: '',
    numero_serie: '',
    site_id: '',
    zone: '',
    etage: '',
    capacite: '',
    agent_extincteur: 'poudre',
    date_installation: new Date().toISOString().split('T')[0],
    date_prochaine_verification: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    periodicite_verification: 'annuelle',
    statut: 'conforme',
    observations: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      
      // Trouver le nom du site
      const site = sites.find(s => s.id === formData.site_id);
      const submitData = {
        ...formData,
        site_name: site?.name || ''
      };

      await axios.post(`${API_URL}/api/ssi/equipements`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erreur création équipement:', error);
      alert('Erreur lors de la création de l\'équipement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6 text-red-600 flex items-center gap-2">
          <Flame className="h-6 w-6" />
          Ajouter un équipement de sécurité incendie
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Type d'équipement */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-2">Type d'équipement *</label>
            <select
              value={formData.type_equipement}
              onChange={(e) => setFormData({ ...formData, type_equipement: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
              required
            >
              <option value="extincteur">🧯 Extincteur</option>
              <option value="detecteur_fumee">💨 Détecteur de fumée</option>
              <option value="detecteur_chaleur">🔥 Détecteur de chaleur</option>
              <option value="alarme_incendie">🚨 Alarme incendie</option>
              <option value="ria">💧 RIA (Robinet Incendie Armé)</option>
              <option value="desenfumage">🌬️ Désenfumage</option>
              <option value="eclairage_securite">💡 Éclairage de sécurité</option>
              <option value="bloc_secours">🔦 Bloc secours</option>
            </select>
          </div>

          {/* Informations produit */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Informations produit</h3>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Marque *"
                value={formData.marque}
                onChange={(e) => setFormData({ ...formData, marque: e.target.value })}
                className="border rounded-lg px-3 py-2"
                required
              />
              <input
                type="text"
                placeholder="Modèle"
                value={formData.modele}
                onChange={(e) => setFormData({ ...formData, modele: e.target.value })}
                className="border rounded-lg px-3 py-2"
              />
              <input
                type="text"
                placeholder="N° de série"
                value={formData.numero_serie}
                onChange={(e) => setFormData({ ...formData, numero_serie: e.target.value })}
                className="border rounded-lg px-3 py-2"
              />
              {formData.type_equipement === 'extincteur' && (
                <>
                  <input
                    type="text"
                    placeholder="Capacité (ex: 6kg, 9L)"
                    value={formData.capacite}
                    onChange={(e) => setFormData({ ...formData, capacite: e.target.value })}
                    className="border rounded-lg px-3 py-2"
                  />
                  <select
                    value={formData.agent_extincteur}
                    onChange={(e) => setFormData({ ...formData, agent_extincteur: e.target.value })}
                    className="border rounded-lg px-3 py-2"
                  >
                    <option value="poudre">Poudre ABC</option>
                    <option value="co2">CO2</option>
                    <option value="eau_pulverisee">Eau pulvérisée</option>
                    <option value="mousse">Mousse</option>
                  </select>
                </>
              )}
            </div>
          </div>

          {/* Localisation */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Localisation</h3>
            <div className="grid grid-cols-3 gap-4">
              <select
                value={formData.site_id}
                onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}
                className="border rounded-lg px-3 py-2"
                required
              >
                <option value="">-- Sélectionner un site --</option>
                {sites.map(site => (
                  <option key={site.id} value={site.id}>{site.name}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Zone/Local *"
                value={formData.zone}
                onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                className="border rounded-lg px-3 py-2"
                required
              />
              <input
                type="text"
                placeholder="Étage"
                value={formData.etage}
                onChange={(e) => setFormData({ ...formData, etage: e.target.value })}
                className="border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          {/* Vérifications */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Vérifications</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm mb-1">Date d'installation</label>
                <input
                  type="date"
                  value={formData.date_installation}
                  onChange={(e) => setFormData({ ...formData, date_installation: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Prochaine vérification</label>
                <input
                  type="date"
                  value={formData.date_prochaine_verification}
                  onChange={(e) => setFormData({ ...formData, date_prochaine_verification: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Périodicité</label>
                <select
                  value={formData.periodicite_verification}
                  onChange={(e) => setFormData({ ...formData, periodicite_verification: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="mensuelle">Mensuelle</option>
                  <option value="trimestrielle">Trimestrielle</option>
                  <option value="semestrielle">Semestrielle</option>
                  <option value="annuelle">Annuelle</option>
                </select>
              </div>
            </div>
          </div>

          {/* Statut et observations */}
          <div className="mb-6">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Statut</label>
                <select
                  value={formData.statut}
                  onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="conforme">Conforme</option>
                  <option value="non_conforme">Non conforme</option>
                  <option value="a_verifier">À vérifier</option>
                  <option value="hors_service">Hors service</option>
                </select>
              </div>
            </div>
            <label className="block text-sm font-semibold mb-2">Observations</label>
            <textarea
              value={formData.observations}
              onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              rows="2"
              placeholder="Remarques, anomalies, informations complémentaires..."
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {saving ? 'Création...' : 'Créer l\'équipement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EquipementsIncendie = () => {
  const [equipements, setEquipements] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatut, setFilterStatut] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEquipement, setSelectedEquipement] = useState(null);

  useEffect(() => {
    loadEquipements();
    loadSites();
  }, [filterType, filterStatut]);

  const loadEquipements = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {};
      if (filterType !== 'all') params.type_equipement = filterType;
      if (filterStatut !== 'all') params.statut = filterStatut;

      const response = await axios.get(`${API_URL}/api/ssi/equipements`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setEquipements(response.data);
    } catch (error) {
      console.error('Erreur chargement équipements:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSites(response.data);
    } catch (error) {
      console.error('Erreur chargement sites:', error);
    }
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'conforme':
        return <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full flex items-center gap-1"><CheckCircle size={12} /> Conforme</span>;
      case 'non_conforme':
        return <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full flex items-center gap-1"><AlertCircle size={12} /> Non conforme</span>;
      case 'a_verifier':
        return <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full flex items-center gap-1"><Clock size={12} /> À vérifier</span>;
      case 'hors_service':
        return <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">Hors service</span>;
      default:
        return <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">{statut}</span>;
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      'extincteur': '🧯 Extincteur',
      'detecteur_fumee': '💨 Détecteur de fumée',
      'detecteur_chaleur': '🔥 Détecteur de chaleur',
      'alarme_incendie': '🚨 Alarme incendie',
      'ria': '💧 RIA (Robinet Incendie Armé)',
      'desenfumage': '🌬️ Désenfumage',
      'eclairage_securite': '💡 Éclairage de sécurité',
      'bloc_secours': '🔦 Bloc secours'
    };
    return types[type] || type;
  };

  const filteredEquipements = equipements.filter(eq =>
    eq.zone.toLowerCase().includes(searchTerm.toLowerCase()) ||
    eq.marque.toLowerCase().includes(searchTerm.toLowerCase()) ||
    eq.numero_serie?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Flame className="h-8 w-8 text-red-600" />
            Équipements Incendie
          </h1>
          <p className="text-gray-600 mt-1">Gestion des équipements de sécurité incendie</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-red-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-red-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Nouvel Équipement
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Rechercher (zone, marque, N° série)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
            />
          </div>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          >
            <option value="all">Tous types</option>
            <option value="extincteur">Extincteurs</option>
            <option value="detecteur_fumee">Détecteurs fumée</option>
            <option value="detecteur_chaleur">Détecteurs chaleur</option>
            <option value="alarme_incendie">Alarmes</option>
            <option value="ria">RIA</option>
            <option value="eclairage_securite">Éclairages</option>
          </select>

          <select
            value={filterStatut}
            onChange={(e) => setFilterStatut(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          >
            <option value="all">Tous statuts</option>
            <option value="conforme">Conforme</option>
            <option value="non_conforme">Non conforme</option>
            <option value="a_verifier">À vérifier</option>
            <option value="hors_service">Hors service</option>
          </select>
        </div>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-gray-900">{equipements.length}</div>
          <div className="text-sm text-gray-600">Total équipements</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-green-600">
            {equipements.filter(e => e.statut === 'conforme').length}
          </div>
          <div className="text-sm text-gray-600">Conformes</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-red-600">
            {equipements.filter(e => e.statut === 'non_conforme').length}
          </div>
          <div className="text-sm text-gray-600">Non conformes</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-orange-600">
            {equipements.filter(e => e.statut === 'a_verifier').length}
          </div>
          <div className="text-sm text-gray-600">À vérifier</div>
        </div>
      </div>

      {/* Liste des équipements */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Chargement...</div>
        ) : filteredEquipements.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Flame className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Aucun équipement trouvé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Type</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Zone</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Marque/Modèle</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">N° Série</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Dernière vérif.</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Prochaine vérif.</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Statut</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredEquipements.map((eq) => (
                  <tr key={eq.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{getTypeLabel(eq.type_equipement)}</td>
                    <td className="px-4 py-3 text-sm font-medium">{eq.zone}</td>
                    <td className="px-4 py-3 text-sm">
                      {eq.marque}
                      {eq.modele && <span className="text-gray-500"> - {eq.modele}</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{eq.numero_serie || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      {eq.date_derniere_verification
                        ? new Date(eq.date_derniere_verification).toLocaleDateString('fr-FR')
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {new Date(eq.date_prochaine_verification).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3">{getStatutBadge(eq.statut)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedEquipement(eq)}
                          className="p-1 hover:bg-blue-50 rounded"
                          title="Voir détails"
                        >
                          <Eye size={16} className="text-blue-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Message si pas d'équipements */}
      {equipements.length === 0 && !loading && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-yellow-600 mx-auto mb-3" />
          <h3 className="font-semibold text-yellow-900 mb-2">Aucun équipement SSI enregistré</h3>
          <p className="text-sm text-yellow-800 mb-4">
            Commencez par ajouter vos équipements de sécurité incendie (extincteurs, détecteurs, alarmes, etc.)
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700"
          >
            Ajouter le premier équipement
          </button>
        </div>
      )}

      {/* Modal Create */}
      {showCreateModal && <CreateEquipementModal sites={sites} onClose={() => setShowCreateModal(false)} onSuccess={loadEquipements} />}
    </div>
  );
};

export default EquipementsIncendie;
